<script setup lang="ts">
/* eslint-disable vue/no-v-html */
import { ref, computed, onMounted, onScopeDispose } from 'vue';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgContainer } from '../../Atoms/Container';
import { PhxsgHeading } from '../../Atoms/Heading';
import { PhxsgText } from '../../Atoms/Text';

interface PhxsgInfoBlockProps {
  title: string;
  text: string;
  headingTag?: string;
  revealLabel: string;
  concealLabel: string;
  lineClamp?: number | undefined;
  containerSize?: string;
}

const { title, text, headingTag = 'h2', revealLabel, concealLabel, lineClamp = 1 } = defineProps<PhxsgInfoBlockProps>();

const truncate = ref(true);
const isOverflowing = ref(false);
const textEl = ref<InstanceType<typeof PhxsgText> | null>(null);

const toggleLabel = computed(() => (truncate.value ? revealLabel : concealLabel));

onMounted(() => {
  const el = textEl.value?.$el;

  if (!el) return;

  const observer = new ResizeObserver(() => {
    if (!truncate.value || el.offsetParent === null) return;
    isOverflowing.value = el.scrollHeight > el.clientHeight;
  });
  observer.observe(el);

  onScopeDispose(() => observer.disconnect());
});
</script>

<template>
  <PhxsgContainer :size="containerSize" with-grid>
    <div class="phxsg-info-block">
      <phxsg-heading v-if="title" :use="headingTag" variant="h3" align="start">
        <span v-html="title"></span>
      </phxsg-heading>

      <phxsg-text ref="textEl" variant="body" size="medium" :line-clamp="truncate ? lineClamp : undefined">
        <span v-html="text"></span>
      </phxsg-text>

      <PhxsgButton
        v-if="isOverflowing"
        variant="link"
        size="small"
        uppercase
        color="primary"
        :label="toggleLabel"
        @click="truncate = !truncate"
      />
    </div>
  </PhxsgContainer>
</template>

<style src="./info-block.css" />
